import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, User, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import * as Dialog from '@radix-ui/react-dialog';

interface NavbarProps {
    onToggleSidebar: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar }) => {
    const { user, logout } = useAuth();
    const [confirmOpen, setConfirmOpen] = useState(false);

    return (
        <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center justify-between h-16 px-6">
                {/* Left section */}
                <div className="flex items-center space-x-4">
                    <button onClick={onToggleSidebar} className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors">
                        <Menu className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Right section */}
                <div className="flex items-center space-x-4">
                    {/* User menu */}
                    <div className="flex items-center space-x-3">
                        <div className="hidden sm:block text-right">
                            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                            <p className="text-xs text-gray-500">{user?.role}</p>
                        </div>

                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                        </div>

                        <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)} className="text-gray-600 hover:text-gray-900">
                            <LogOut className="w-4 h-4 mr-2" />
                            <span className="hidden sm:inline">Logout</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Dialog */}
            <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
                    <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl border border-gray-200 w-[90vw] max-w-sm p-6 focus:outline-none">
                        <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm Logout</Dialog.Title>
                        <Dialog.Description className="mt-1 text-sm text-gray-600">Are you sure you want to logout?</Dialog.Description>
                        <div className="mt-6 flex items-center justify-end gap-2">
                            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                                Stay Logged In
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    setConfirmOpen(false);
                                    logout();
                                }}
                            >
                                Yes
                            </Button>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </motion.header>
    );
};

export default Navbar;
